import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { EnvelopeSimple, PaperPlaneTilt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Per-partner subscription control for the weekly Client Health Digest.
 * Renders as a compact strip on the ClientDetail page beneath the health
 * storyline so partners subscribe with a single tap. State is fetched from
 * /client-health-digest/clients/{cid}/subscription and cadence changes
 * upsert idempotently.
 */
export default function ClientHealthDigestToggle({ clientId, clientName }) {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [cadence, setCadence] = useState("weekly");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/client-health-digest/clients/${clientId}/subscription`);
      setSub(data);
      if (data?.cadence) setCadence(data.cadence);
    } catch { /* silent */ }
  };
  useEffect(() => { if (clientId) load(); }, [clientId]);

  const subscribed = !!sub?.cadence;

  const subscribe = async () => {
    // Derive the recipient from the authenticated session — never trust the
    // GET response shape to carry it. Prevents a null email being sent if
    // the GET contract ever changes.
    const email = user?.email;
    if (!email) return toast.error("No authenticated email — please re-login");
    setBusy(true);
    try {
      const { data } = await api.put(`/client-health-digest/clients/${clientId}/subscription`, {
        email,
        cadence,
      });
      setSub(data);
      toast.success(`Weekly health digest for ${clientName} — ${cadence}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Subscription failed");
    } finally { setBusy(false); }
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      await api.delete(`/client-health-digest/clients/${clientId}/subscription`);
      setSub({ ...sub, cadence: null, last_sent_at: null });
      toast.success("Unsubscribed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unsubscribe failed");
    } finally { setBusy(false); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/client-health-digest/run`);
      toast.success(`Sent ${data.sent || 0} digest${data.sent === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-border bg-card p-4 md:p-5" data-testid="client-health-digest-toggle">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.24em] text-eyebrow flex items-center gap-1.5">
            <EnvelopeSimple size={11} weight="fill" /> Weekly relationship pulse
          </div>
          <div className="mt-1 text-sm text-foreground">
            {subscribed ? (
              <>Subscribed · <span className="text-brass">{cadence}</span>{sub?.last_sent_at && <> · last sent {new Date(sub.last_sent_at).toLocaleDateString()}</>}</>
            ) : (
              "Get a proactive relationship pulse for this partner organization each week — no need to open the app."
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={cadence} onValueChange={setCadence}>
            <SelectTrigger data-testid="digest-cadence-select" className="rounded-none bg-secondary/40 w-32 h-9 text-[0.6875rem]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="biweekly">Biweekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
          {subscribed ? (
            <>
              <Button
                data-testid="digest-run-now"
                onClick={runNow}
                disabled={busy}
                variant="outline"
                size="sm"
                className="rounded-none h-9 text-[0.6875rem] border-primary/60 text-primary hover:bg-primary/10"
              >
                <PaperPlaneTilt size={11} weight="bold" className="mr-1" /> Send now
              </Button>
              <Button
                data-testid="digest-unsubscribe"
                onClick={unsubscribe}
                disabled={busy}
                variant="outline"
                size="sm"
                className="rounded-none h-9 text-[0.6875rem] border-destructive/60 text-destructive hover:bg-destructive/10"
              >
                Unsubscribe
              </Button>
            </>
          ) : (
            <Button
              data-testid="digest-subscribe"
              onClick={subscribe}
              disabled={busy}
              size="sm"
              className="rounded-none h-9 bg-primary text-primary-foreground text-[0.6875rem]"
            >
              Subscribe
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
